import { del, get, patch, post } from "@libs:axios";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { omit } from "lodash";
import { CamelCasedPropertiesDeep } from "type-fest";

import {
  OrganizationsCreateSchema,
  OrganizationsGetSchema,
  OrganizationsUpdateSchema,
} from "@:openapi";

export interface Organizations {
  GET_ALL: {
    Response: CamelCasedPropertiesDeep<Array<OrganizationsGetSchema>>;
  };
  GET_ONE: {
    Request: { id: string };
    Response: CamelCasedPropertiesDeep<OrganizationsGetSchema>;
  };
  POST: {
    Request: CamelCasedPropertiesDeep<OrganizationsCreateSchema>;
    Response: CamelCasedPropertiesDeep<OrganizationsGetSchema>;
  };
  PATCH: {
    RequestBody: CamelCasedPropertiesDeep<OrganizationsUpdateSchema>;
    RequestPath: { id: string };
    Response: CamelCasedPropertiesDeep<OrganizationsGetSchema>;
  };
  DELETE: {
    RequestPath: { id: string };
    Response: CamelCasedPropertiesDeep<OrganizationsGetSchema>;
  };
}

export const organizationQueryKey = createQueryKeys("organization", {
  getOne: (params: Organizations["GET_ONE"]["Request"]) => [params],
  getAll: null,
});

export const useQueryGetOneOrganization = (
  params: Organizations["GET_ONE"]["Request"],
  options?: Omit<
    UseQueryOptions<Organizations["GET_ONE"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<Organizations["GET_ONE"]["Response"], AxiosError>({
    queryKey: organizationQueryKey.getOne(params).queryKey,
    queryFn: () => {
      return get<Organizations["GET_ONE"]["Response"]>(
        `/api/organizations/${params.id}`,
      );
    },
    ...options,
  });
};

export const useQueryGetAllOrganizations = (
  options?: Omit<
    UseQueryOptions<Organizations["GET_ALL"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<Organizations["GET_ALL"]["Response"], AxiosError>({
    queryKey: organizationQueryKey.getAll.queryKey,
    queryFn: () => {
      return get<Organizations["GET_ALL"]["Response"]>("/api/organizations");
    },
    ...options,
  });
};

export const useMutationCreateOrganization = (
  options?: UseMutationOptions<
    Organizations["POST"]["Response"],
    AxiosError,
    CamelCasedPropertiesDeep<OrganizationsCreateSchema>
  >,
) => {
  return useMutation({
    mutationFn: (data: CamelCasedPropertiesDeep<OrganizationsCreateSchema>) => {
      return post<Organizations["POST"]["Response"]>(
        "/api/organizations",
        data,
      );
    },
    ...options,
  });
};

export const useMutationUpdateOrganization = (
  options?: UseMutationOptions<
    Organizations["PATCH"]["Response"],
    AxiosError,
    Organizations["PATCH"]["RequestBody"] &
      Organizations["PATCH"]["RequestPath"]
  >,
) => {
  return useMutation({
    mutationFn: (
      data: Organizations["PATCH"]["RequestBody"] &
        Organizations["PATCH"]["RequestPath"],
    ) => {
      return patch<Organizations["PATCH"]["Response"]>(
        `/api/organizations/${data.id}`,
        omit(data, ["id"]),
      );
    },
    ...options,
  });
};

export const useMutationDeleteOrganization = (
  options?: UseMutationOptions<
    Organizations["DELETE"]["Response"],
    AxiosError,
    Organizations["DELETE"]["RequestPath"]
  >,
) => {
  return useMutation({
    mutationFn: (params: Organizations["DELETE"]["RequestPath"]) => {
      return del<Organizations["DELETE"]["Response"]>(
        `/api/organizations/${params.id}`,
      );
    },
    ...options,
  });
};
